import type { PODApi as PODMallCouponApi } from '@mathflat/domain/@entities/PODMall/Coupon/api'
import { Entity as CouponEntity } from '@mathflat/domain/@entities/PODMall/Coupon/dto'
import { makeAutoObservable } from 'mobx'

import { podMallController } from '@/controller/podMall.controller'
import { commonErrorMessage, toastSuccess } from '@/utils/toast'

export default class PODMallCouponService {
  couponList: CouponEntity.Coupon[] = []
  couponRandomCode: string | null = null
  constructor() {
    makeAutoObservable(this)
  }

  get couponCountStr() {
    const couponCount = this.couponList.length
    if (couponCount > 99) return '99+'
    if (couponCount === 0) return ''
    return `${couponCount}`
  }

  updateRandomCouponCode = (code: string) => {
    this.couponRandomCode = code
  }

  private async _fetchCouponList() {
    const { data } = await podMallController.getCoupon()
    return data.map((coupon) => new CouponEntity.Coupon(coupon))
  }

  private async _fetchProductDiscountEvent() {
    const response = await podMallController.getProductDiscountEvent()

    if (!response) return null

    return new ProductDiscountCoupon({
      ...response.data,
      conditionName: '',
      usable: true,
      benefitValue: response.data.discountAmount,
      usageStatus: 'UNUSED',
    })
  }

  async fetchSetCouponList() {
    try {
      const [productDiscountEvent, couponList] = await Promise.all([
        this._fetchProductDiscountEvent(),
        this._fetchCouponList(),
      ])

      if (productDiscountEvent) {
        this.couponList = [productDiscountEvent, ...couponList]
      } else {
        this.couponList = couponList
      }
    } catch (error) {
      console.error(error)
    }
  }

  async fetchCouponListByCode({ code }: { code: string }) {
    const { data } = await podMallController.getCoupon({ code })
    return data.map((coupon) => new CouponEntity.Coupon(coupon))
  }

  async registerCoupon(
    userCouponId: number,
    payload: PODMallCouponApi.Request.UserCouponByCodeRequest,
  ) {
    try {
      const data = await podMallController.postUserCouponByCode(userCouponId, payload)
      await this.fetchSetCouponList()
      toastSuccess('쿠폰이 추가되었습니다.')
      return data
    } catch (e) {
      commonErrorMessage(e)
    }
  }
}

export class ProductDiscountCoupon extends CouponEntity.Coupon {
  static PRODUCT_DISCOUNT_COUPON_ID = -1

  override benefitType: CouponEntity.Coupon['benefitType'] = 'PRODUCT_DISCOUNT'

  constructor(coupon: Omit<CouponEntity.Coupon, 'benefitType' | 'id'>) {
    super({
      ...coupon,
      id: ProductDiscountCoupon.PRODUCT_DISCOUNT_COUPON_ID,
      benefitType: 'PRODUCT_DISCOUNT',
    })
  }
}
